import { useCallback, useEffect, useRef, useState } from "react";

/**
 * DebouncedNotes - Textarea component with debounced onChange
 * 
 * Prevents excessive state updates when typing. Waits 500ms after
 * user stops typing before calling onChange callback.
 * 
 * @param {string} value - Current value
 * @param {Function} onChange - Callback when value changes (debounced)
 * @param {Object} props - Other textarea props
 */
export default function DebouncedNotes({ value, onChange, ...props }) {
  const [localValue, setLocalValue] = useState(value || "");
  const debounceTimer = useRef(null);

  // Update local value when prop value changes (e.g., when date changes)
  useEffect(() => {
    setLocalValue(value || "");
  }, [value]);

  const handleChange = useCallback(
    (e) => {
      const newValue = e.target.value;
      setLocalValue(newValue);

      // Clear existing timer
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      // Set new timer - call onChange after 500ms of inactivity
      debounceTimer.current = setTimeout(() => {
        onChange(newValue);
      }, 500);
    },
    [onChange]
  );

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  return (
    <div>
      <label className="text-xs text-zinc-600 uppercase tracking-widest font-semibold mb-2 block">
        Reflection / notes
      </label>
      <textarea
        value={localValue}
        onChange={handleChange}
        placeholder="Anything you want to reflect on today…"
        className="w-full rounded-xl bg-[#09090B] border border-zinc-800 text-zinc-300 px-3 py-2 text-base font-base outline-none focus:ring-2 focus:ring-[#10B981] min-h-[44px]"
        rows={3}
        {...props}
      />
    </div>
  );
}
